/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { toast } from 'react-toastify'

import axios, { AxiosError, AxiosInstance } from 'axios'
import http from 'http'
import https from 'https'
import jwtDecode from 'jwt-decode'

let gatewayApi: AxiosInstance
const httpAgent = new http.Agent({ keepAlive: true })
const httpsAgent = new https.Agent({ keepAlive: true })

type CorpoMensagemApi = {
  mensagem?: string
  Mensagem?: string
  errors?: string[]
  Errors?: string[]
}

type ApiErro = AxiosError<CorpoMensagemApi[] | CorpoMensagemApi>

const isApiError = (err: ApiErro | Error): err is ApiErro => {
  return (err as ApiErro).response !== undefined
}

const mensagemDeItem = (item: unknown): string | undefined => {
  if (!item || typeof item !== 'object') return undefined

  const corpo = item as CorpoMensagemApi
  const valor = corpo.mensagem ?? corpo.Mensagem

  return typeof valor === 'string' && valor.trim().length > 0
    ? valor
    : undefined
}

const extrairMensagensDoCorpo = (data: unknown): string[] => {
  if (data == null) return []

  if (Array.isArray(data)) {
    return data
      .map(mensagemDeItem)
      .filter((mensagem): mensagem is string => !!mensagem)
  }

  // Formato do CustomResponse (base dos controllers): { success: false, errors: string[] }
  const corpo = data as CorpoMensagemApi
  const listaDeErros = corpo.errors ?? corpo.Errors
  if (Array.isArray(listaDeErros) && listaDeErros.length > 0) {
    return listaDeErros.filter(
      (mensagem): mensagem is string =>
        typeof mensagem === 'string' && mensagem.trim().length > 0
    )
  }

  const unica = mensagemDeItem(data)
  return unica ? [unica] : []
}

const obterMensagensDaApi = (err: ApiErro): Promise<string[]> => {
  if (err.config?.responseType === 'blob') {
    return new Promise(resolve => {
      const fr = new FileReader()

      fr.onloadend = function () {
        try {
          resolve(extrairMensagensDoCorpo(JSON.parse(String(this.result))))
        } catch {
          resolve([])
        }
      }

      fr.readAsText((err.response?.data as unknown) as Blob)
    })
  }

  return Promise.resolve(extrairMensagensDoCorpo(err.response?.data))
}

export const getApiError = (err: ApiErro | Error): Promise<string[]> => {
  if (isApiError(err)) {
    return obterMensagensDaApi(err).then(mensagens => {
      if (mensagens.length > 0) return mensagens
      if (err.message) return [err.message]
      return []
    })
  }

  if (err.message) {
    return Promise.resolve([err.message])
  }

  return Promise.resolve([])
}

const toastarErroDaApi = (axiosError: ApiErro) => {
  const status = axiosError.response?.status

  obterMensagensDaApi(axiosError).then(mensagens => {
    if (mensagens.length > 0) {
      for (const mensagem of mensagens) {
        toast(mensagem, {
          type: 'error'
        })
      }
      return
    }

    toast(`Não foi possível salvar os dados (HTTP ${status})`, {
      type: 'error'
    })
  })
}

// A claim so existe em tokens de impersonate; usuarios/tokens normais nao a tem.
const tokenEDeImpersonate = (token: string): boolean => {
  try {
    return jwtDecode<{ impersonated?: string }>(token)?.impersonated === 'true'
  } catch {
    return false
  }
}

const instanciarApi = (
  baseURL: string,
  token: string,
  sair: (semRedirecionamento?: boolean) => Promise<void>
) => {
  const instancia = axios.create({
    baseURL,
    responseType: 'json',
    httpAgent,
    httpsAgent
  })

  instancia.defaults.headers.Authorization = `Bearer ${token}`

  const impersonando = token ? tokenEDeImpersonate(token) : false

  instancia.interceptors.response.use(
    respostaApi => respostaApi,
    erro => {
      const axiosError = erro as ApiErro
      if (axiosError.isAxiosError) {
        switch (axiosError.response?.status) {
          case 400:
          case 500:
            toastarErroDaApi(axiosError)
            break
          case 401:
            if (window.location.pathname !== '/entrar') {
              sair(!!window.location.pathname.startsWith('/matricula'))
            }
            break
          case 403:
            // Durante uma sessao de impersonate, 403 e o comportamento
            // esperado (FiltroDeImpersonation bloqueando escrita, ou o
            // usuario-alvo sem a role exigida) - nao e uma falha de sessao,
            // entao nao redireciona, so mostra a mensagem em toast. Fora de
            // impersonate, mantem o redirecionamento de sempre. O erro segue
            // propagado (throw abaixo) para quem chamou tratar, se quiser.
            if (impersonando) {
              toastarErroDaApi(axiosError)
            } else {
              window.location.href = '/403'
            }
            break
          default:
            break
        }
      }
      throw erro
    }
  )

  return instancia
}

const criar = (
  token: string,
  sair: (semRedirecionamento?: boolean) => Promise<void> | undefined
) => {
  gatewayApi = instanciarApi(
    `${process.env.REACT_APP_URL_GATEWAY}/api`,
    token,
    sair
  )
}

export { criar, gatewayApi }
